import {
  CloudUploadOutlined,
  DownOutlined,
  FileDoneOutlined,
  FileTextOutlined
} from "@ant-design/icons"
import { Button, Dropdown, Empty, Flex, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { TEMPLATE_DOCUMENT_TYPES } from "../../../DocumentTemplates/constants/template-document-type.tsx"
import { uploadProposal } from "../../../Proposals/redux/proposals.thunks.ts"
import { LeadViewContracts } from "../../LeadViewPage/components/LeadViewContract/LeadViewContracts.tsx"
import { LeadViewContractsCreateTrigger } from "../../LeadViewPage/components/LeadViewContract/LeadViewContractsCreateTrigger.tsx"
import { LeadViewDocumentUpload } from "../../LeadViewPage/components/LeadViewDocument/LeadViewDocumentUpload.tsx"
import { LeadViewProposals } from "../../LeadViewPage/components/LeadViewProposal/LeadViewProposals.tsx"
import { LeadViewTemplatesModalTrigger } from "../../LeadViewPage/components/LeadViewTemplatesModal/LeadViewTemplatesModalTrigger.tsx"
import type { LeadProposal } from "../../types/lead.documents.types.ts"
import type { Lead } from "../../types/lead.types.ts"
import { WorkspaceCard } from "../cards/WorkspaceCard.tsx"
import { DocumentCreateMenuOption } from "./DocumentCreateMenuOption.tsx"
import { leadDocumentVisibility } from "./lead-documents-visibility.ts"
import { OverviewCardIcon } from "../overview/OverviewCardIcon.tsx"

const { Text } = Typography

interface LeadDocumentsTabProps {
  lead: Lead
  canEdit?: boolean
}

/** Icon + title + subtitle, built as one node since `WorkspaceCard.title` has no separate subtitle slot. */
function DocumentSectionTitle({
  icon,
  accent,
  title,
  description
}: {
  icon: React.ReactNode
  accent: "indigo" | "green"
  title: string
  description: string
}) {
  return (
    <Flex align={"center"} gap={"middle"}>
      <OverviewCardIcon icon={icon} accent={accent} size={40} />
      <Flex vertical gap={0}>
        <span>{title}</span>
        <Text type={"secondary"} className={"text-xs font-normal"}>
          {description}
        </Text>
      </Flex>
    </Flex>
  )
}

/**
 * Relocated from the Overview grid into its own tab — the mockup's cards had
 * no slot for a document workflow this substantial, and one card per section
 * (rather than the pair of `<section>`s inside a single card) matches how
 * every other tab in this workspace composes itself (see `LeadScopeTab`).
 * Each section exposes a single "Create" dropdown — upload lives inside it
 * as another creation option rather than a separate top-level button — and
 * the stage gating is otherwise unchanged from `LeadOverviewDocuments`.
 */
function LeadDocumentsTab({ lead, canEdit }: LeadDocumentsTabProps) {
  const { t } = useTranslation()
  const leadId = lead?.id
  const [proposalMenuOpen, setProposalMenuOpen] = useState(false)

  const {
    isProposalStage,
    canCreateProposal,
    showProposals,
    canCreateContract,
    canUploadContract,
    showContracts
  } = leadDocumentVisibility(lead)

  return (
    <Flex vertical gap={"middle"} data-testid={"lead-documents-tab"}>
      {showProposals && (
        <WorkspaceCard
          cardId={"documents-proposals"}
          title={
            <DocumentSectionTitle
              icon={<FileTextOutlined />}
              accent={"indigo"}
              title={t("leadWorkspace.cards.documents.proposals")}
              description={t(
                "leadWorkspace.cards.documents.proposalsDescription"
              )}
            />
          }
          ariaTitle={t("leadWorkspace.cards.documents.proposals")}
          extra={
            canEdit &&
            canCreateProposal && (
              <Dropdown
                trigger={["click"]}
                open={proposalMenuOpen}
                onOpenChange={setProposalMenuOpen}
                popupRender={() => (
                  <div
                    className={
                      "w-80 rounded-card border border-border bg-white p-2 shadow-lg"
                    }
                  >
                    <Text
                      type={"secondary"}
                      className={"text-xs px-2 block mb-1"}
                    >
                      {t(
                        "leadWorkspace.cards.documents.createProposalMenuTitle"
                      )}
                    </Text>
                    <LeadViewTemplatesModalTrigger
                      type={TEMPLATE_DOCUMENT_TYPES.PROPOSAL}
                      leadId={leadId}
                      render={(onClick) => (
                        <DocumentCreateMenuOption
                          icon={<FileTextOutlined />}
                          accent={"indigo"}
                          title={t(
                            "leadWorkspace.cards.documents.fromProposalTemplate"
                          )}
                          description={t(
                            "leadWorkspace.cards.documents.fromProposalTemplateDescription"
                          )}
                          onClick={() => {
                            setProposalMenuOpen(false)
                            onClick()
                          }}
                        />
                      )}
                    />
                    <LeadViewDocumentUpload<LeadProposal>
                      leadId={leadId}
                      documentType={"proposal"}
                      uploadThunk={uploadProposal}
                      render={() => (
                        <DocumentCreateMenuOption
                          icon={<CloudUploadOutlined />}
                          accent={"orange"}
                          title={t(
                            "leadWorkspace.cards.documents.uploadProposalOption"
                          )}
                          description={t(
                            "leadWorkspace.cards.documents.uploadProposalOptionDescription"
                          )}
                        />
                      )}
                    />
                  </div>
                )}
              >
                <Button type={"primary"}>
                  {t("leadWorkspace.cards.documents.newProposal")}
                  <DownOutlined className={"text-xs"} />
                </Button>
              </Dropdown>
            )
          }
        >
          {lead?.proposals?.length ? (
            <LeadViewProposals
              isUpdateAllowed={isProposalStage}
              viewOnly={!canEdit}
              proposals={lead?.proposals}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("leadWorkspace.cards.documents.emptyProposals")}
            />
          )}
        </WorkspaceCard>
      )}

      {showContracts && (
        <WorkspaceCard
          cardId={"documents-contracts"}
          title={
            <DocumentSectionTitle
              icon={<FileDoneOutlined />}
              accent={"green"}
              title={t("leadWorkspace.cards.documents.contracts")}
              description={t(
                "leadWorkspace.cards.documents.contractsDescription"
              )}
            />
          }
          ariaTitle={t("leadWorkspace.cards.documents.contracts")}
          extra={
            canEdit &&
            (canCreateContract || canUploadContract) && (
              <LeadViewContractsCreateTrigger
                leadId={leadId}
                proposals={lead?.proposals}
                canCreateContract={canCreateContract}
                canUploadContract={canUploadContract}
              />
            )
          }
        >
          {lead?.contracts?.length ? (
            <LeadViewContracts
              isUpdateAllowed={canCreateContract}
              viewOnly={!canEdit}
              contracts={lead?.contracts}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("leadWorkspace.cards.documents.emptyContracts")}
            />
          )}
        </WorkspaceCard>
      )}
    </Flex>
  )
}

export { LeadDocumentsTab }
